import React, { useEffect, useRef, useState } from 'react';
import { Button as ReactButton } from 'devextreme-react/button';
import ButtonGroup from 'devextreme-react/button-group';
import Slider from 'devextreme-react/slider';
import TagBox from 'devextreme-react/tag-box';
import { VisTimelineViewProps } from '../hooks/useVisTimelineController';
import { DepthMode } from '../../shared/progressData';
import EventTooltip from '../../../../shared/timeline/EventTooltip';
import StatusLegend from '../../shared/StatusLegend';
import UnscheduledBanner from '../../shared/UnscheduledBanner';

interface DepthOption {
    value: DepthMode;
    text: string;
    icon: string;
    hint: string;
}

const DEPTH_OPTIONS: DepthOption[] = [
    { value: 'modules', text: 'Modules', icon: 'box', hint: 'One row per module (aggregated dates).' },
    { value: 'phases', text: 'Tasks', icon: 'orderedlist', hint: 'One row per task.' },
    { value: 'steps', text: 'Subtasks', icon: 'hierarchy', hint: 'Full hierarchy: module → task → subtask rows.' },
];

const VisTimelineView: React.FC<VisTimelineViewProps> = (p) => {
    const captureRef = useRef<HTMLDivElement>(null);
    const hScrollRef = useRef<HTMLDivElement>(null);
    // Guards an event loop: when we set scrollLeft to mirror vis's window, the resulting scroll event
    // would otherwise feed back into setTimelineWindow and fight the user's actual interaction.
    const skipScrollRef = useRef(false);
    // Zoom slider: the visible value is the controller's zoomPercent when idle, or a locally-tracked
    // drag value during a drag. Release is detected by an idle timeout (~200ms after the last
    // onValueChanged) — DX swallows pointerup so a window-level listener never fires reliably. The
    // idle commit *only* calls setZoomPercent; we wait for the round-trip (vis → rangechanged →
    // windowState → recomputed zoomPercent) to land before clearing draggedValue. If we cleared
    // immediately, there'd be one render where sliderValue falls back to a stale p.zoomPercent and
    // the thumb visibly snaps to the starting position before bouncing to the committed one.
    const [draggedValue, setDraggedValue] = useState<number | null>(null);
    const draggedValueRef = useRef<number | null>(null);
    const idleTimerRef = useRef<number | null>(null);
    const setZoomPercentRef = useRef(p.setZoomPercent);
    useEffect(() => { setZoomPercentRef.current = p.setZoomPercent; }, [p.setZoomPercent]);
    useEffect(() => () => {
        if (idleTimerRef.current !== null) window.clearTimeout(idleTimerRef.current);
    }, []);
    // Once p.zoomPercent moves (either from our commit's round-trip or from Ctrl+scroll/drag-pan),
    // the slider should derive from it again — drop the local drag value.
    useEffect(() => {
        if (draggedValueRef.current !== null) {
            draggedValueRef.current = null;
            setDraggedValue(null);
        }
    }, [p.zoomPercent]);
    const sliderValue = draggedValue ?? p.zoomPercent;

    // Mirror vis's window onto the scrollbar: inner div width = (fullRange / window) * containerWidth,
    // so the native thumb width naturally reflects the zoom level and its position reflects the pan.
    useEffect(() => {
        const el = hScrollRef.current;
        const inner = el?.firstElementChild as HTMLElement | null;
        if (!el || !inner) return;
        const R = p.dataRangeMax.getTime() - p.dataRangeMin.getTime();
        const W = p.windowEnd.getTime() - p.windowStart.getTime();
        if (R <= 0 || W >= R) {
            inner.style.width = '100%';
            return;
        }
        const containerW = el.clientWidth;
        const innerW = (R / W) * containerW;
        inner.style.width = `${innerW}px`;
        const maxScroll = innerW - containerW;
        if (maxScroll <= 0) return;
        const proportion = (p.windowStart.getTime() - p.dataRangeMin.getTime()) / (R - W);
        skipScrollRef.current = true;
        el.scrollLeft = Math.max(0, Math.min(maxScroll, proportion * maxScroll));
        requestAnimationFrame(() => { skipScrollRef.current = false; });
    }, [p.windowStart, p.windowEnd, p.dataRangeMin, p.dataRangeMax]);

    const onHScroll = () => {
        if (skipScrollRef.current) return;
        const el = hScrollRef.current;
        const inner = el?.firstElementChild as HTMLElement | null;
        if (!el || !inner) return;
        const R = p.dataRangeMax.getTime() - p.dataRangeMin.getTime();
        const W = p.windowEnd.getTime() - p.windowStart.getTime();
        if (R <= 0 || W >= R) return;
        const containerW = el.clientWidth;
        const maxScroll = inner.offsetWidth - containerW;
        if (maxScroll <= 0) return;
        const proportion = el.scrollLeft / maxScroll;
        const newStart = p.dataRangeMin.getTime() + proportion * (R - W);
        p.setTimelineWindow(new Date(newStart), new Date(newStart + W));
    };

    if (p.loading) {
        return <div className="container-fluid panelBody"><div>Loading…</div></div>;
    }
    if (!p.hasData) {
        return <div className="container-fluid panelBody"><div>No enabled modules with phase data.</div></div>;
    }
    return (
        <div className="container-fluid panelBody">
            <style>{`
                /* Match the legacy timeline's chart font (Utils TIMELINE.FONTNAMES) so bar/row/axis
                   labels read the same; it's also the DX widget family, so the tooltips line up too. */
                .pt-vis-container {
                    font-family: "Helvetica Neue","Segoe UI",helvetica,verdana,sans-serif;
                }
                .pt-event-tick {
                    background: transparent !important;
                    border: 0 !important;
                }
                /* Event ticks are 'box' items; hide the box's dot and vertical line so only the
                   triangle shows (matches how clusters are styled below). */
                .pt-event-tick .vis-dot,
                .pt-event-tick .vis-line { display: none !important; }
                .pt-event-tick .vis-item-content { padding: 0 !important; }
                /* Original-style inverted-triangle event marker. */
                .pt-tick-tri {
                    display: inline-block;
                    line-height: 0;
                    transform: translateY(-3px);
                }
                .pt-tick-tri svg { overflow: visible; display: block; }
                .pt-tick-tri polygon {
                    fill: #2196F3;
                    stroke: #2196F3;
                    stroke-width: 1;
                }
                /* Note-only day: a darker-blue triangle so notes read distinctly from file/comment events. */
                .pt-tick-tri.pt-tick-note polygon {
                    fill: #0D47A1;
                    stroke: #0D47A1;
                }
                /* Both notes and events on the same day: standard fill, outlined in the darker note blue. */
                .pt-tick-tri.pt-tick-both polygon {
                    fill: #2196F3;
                    stroke: #0D47A1;
                    stroke-width: 3;
                }
                .pt-tick-tri:hover polygon { stroke: #000; }
                .pt-tick-tri .pt-tri-count {
                    fill: #fff;
                    font-family: Arial, sans-serif;
                    font-size: 9px;
                    font-weight: 700;
                    pointer-events: none;
                }
                /* Match the system (DevExtreme dx.light) tooltip/popup look instead of vis's default
                   beige Verdana box: white surface, subtle border, soft shadow, rounded. Font matches
                   the DX widget defaults (same as the legacy timeline's DX tooltips) — native vis
                   tooltips can't inherit the DX widget font, so the values are set explicitly here. */
                .pt-vis-container .vis-tooltip {
                    background-color: #fff !important;
                    color: #333 !important;
                    border: 1px solid #ddd !important;
                    border-radius: 4px !important;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18) !important;
                    padding: 8px 12px !important;
                    font-family: "Helvetica Neue","Segoe UI",helvetica,verdana,sans-serif !important;
                    font-size: 14px !important;
                    line-height: 1.35715 !important;
                    color-scheme: light;
                    max-width: 320px !important;
                    white-space: normal !important;
                    pointer-events: none;
                }
                /* The event tooltip panel's CSS lives with the shared EventTooltip component. */
                .pt-tick-bubble {
                    display: inline-flex;
                    align-items: center;
                    gap: 3px;
                    padding: 2px 6px;
                    background: rgba(33, 150, 243, 0.12);
                    border: 1px solid #2196F3;
                    border-radius: 10px;
                    color: #1565C0;
                    line-height: 1;
                    white-space: nowrap;
                    transform: translateY(2px);
                }
                .pt-tick-bubble i { font-size: 12px; }
                .pt-tick-count {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 15px;
                    height: 15px;
                    padding: 0 3px;
                    box-sizing: border-box;
                    font-size: 10px;
                    font-weight: 700;
                    background: #2196F3;
                    color: #fff;
                    border-radius: 50%;
                    line-height: 1;
                    margin-left: 1px;
                }
                /* Bar labels stay visible even when the bar is narrower than its text. */
                .pt-vis-container .vis-item.pt-bar { overflow: visible !important; }
                .pt-vis-container .vis-item.pt-bar .vis-item-overflow { overflow: visible !important; }
                .pt-vis-container .vis-item.pt-bar .vis-item-content { white-space: nowrap; }
                /* A vis cluster (overlapping ticks collapsed at a zoomed-out scale) renders the same inner
                   .pt-tick-bubble as a single tick, so make the cluster element itself transparent. */
                .pt-vis-container .vis-item.vis-cluster {
                    background: transparent !important;
                    border: 0 !important;
                    box-shadow: none !important;
                }
                .pt-vis-container .vis-item.vis-cluster .vis-item-content { padding: 0 !important; }
                .pt-vis-container .vis-item.vis-cluster .vis-dot,
                .pt-vis-container .vis-item.vis-cluster .vis-line { display: none !important; }
                .pt-vis-container .vis-item.vis-background {
                    background-image: repeating-linear-gradient(
                        135deg,
                        rgba(150,150,150,0.18) 0px,
                        rgba(150,150,150,0.18) 4px,
                        rgba(150,150,150,0.06) 4px,
                        rgba(150,150,150,0.06) 8px
                    );
                }
                .pt-vis-container .vis-item.pt-clickable { cursor: pointer; }
                .pt-vis-container .vis-item.pt-row-sizer {
                    visibility: hidden;
                    pointer-events: none;
                    height: 28px !important;
                    min-height: 28px !important;
                    box-sizing: content-box;
                    background: transparent !important;
                    border: 0 !important;
                }
                .pt-vis-container .vis-labelset .vis-label {
                    display: flex;
                    align-items: center;
                }
                /* All non-parent labels get the arrow's 15px reserved so every level lines up. */
                .pt-vis-container .vis-labelset .vis-label:not(.vis-nesting-group) {
                    padding-left: 21px !important;
                }
                /* Parent labels: leave 6px before the arrow so it doesn't touch the panel edge. */
                .pt-vis-container .vis-labelset .vis-label.vis-nesting-group {
                    padding-left: 6px !important;
                }
                .pt-vis-container .vis-labelset .vis-label .vis-inner {
                    padding: 5px 5px 5px 8px !important;
                    /* Keep titles on one line — a wrapped (2-line) label makes only that row taller
                       than the rest (e.g. long names like "Schematic Design"), which leaves a gap
                       under its event triangle. vis auto-sizes the panel wider to fit single lines. */
                    white-space: nowrap;
                }
                .pt-vis-toolbar {
                    display: flex;
                    flex-flow: row wrap;
                    gap: 18px;
                    align-items: center;
                    padding: 8px 0;
                    font-size: 12px;
                }
                .pt-vis-toolbar .dx-button-text,
                .pt-vis-toolbar .dx-checkbox-text,
                .pt-vis-toolbar .dx-texteditor-input,
                .pt-vis-toolbar .dx-tag-content,
                .pt-vis-toolbar .dx-placeholder {
                    font-size: 12px;
                }
                .pt-toolbar-group {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                }
                .pt-toolbar-label {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 12px;
                    font-weight: 600;
                    color: #444;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .pt-help {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 15px;
                    height: 15px;
                    border-radius: 50%;
                    background: #e0e0e0;
                    color: #555;
                    font-size: 10px;
                    font-weight: 700;
                    cursor: help;
                    text-transform: none;
                    letter-spacing: 0;
                }
                .pt-help:hover { background: #bdbdbd; color: #fff; }
                .pt-toolbar-group .pt-filter { min-width: 280px; }
                .pt-toolbar-actions {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    margin-left: auto;
                }
                .pt-toggle-group {
                    display: inline-flex;
                    align-items: center;
                }
                .pt-toggle-group .dx-button {
                    position: relative;
                    border-radius: 0;
                }
                .pt-toggle-group .dx-button + .dx-button {
                    margin-left: -1px;
                }
                .pt-toggle-group .dx-button:first-child {
                    border-top-left-radius: 4px;
                    border-bottom-left-radius: 4px;
                }
                .pt-toggle-group .dx-button:last-child {
                    border-top-right-radius: 4px;
                    border-bottom-right-radius: 4px;
                }
                /* Filled (on) button overlaps its neighbors so its full border shows. */
                .pt-toggle-group .pt-toggle.pt-toggle-on {
                    z-index: 1;
                    background-color: #e0e0e0 !important;
                    border-color: #888 !important;
                }
                .pt-toggle-group .pt-toggle.pt-toggle-on .dx-button-text {
                    color: #000 !important;
                }
                .pt-toggle-group .pt-toggle.pt-toggle-off {
                    background-color: #fff !important;
                    border-color: #ccc !important;
                }
                .pt-toggle-group .pt-toggle.pt-toggle-off .dx-button-text {
                    color: #666 !important;
                }
                .pt-vis-container .vis-group-level-unknown-but-gte1 { border: 0 !important; }
                .pt-vis-container .vis-current-time {
                    background: linear-gradient(to bottom, transparent 0, transparent 28px, #ff5722 28px) !important;
                }
                .pt-vis-container .vis-current-time::before {
                    content: attr(data-today);
                    position: absolute;
                    top: 12px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: #ff5722;
                    color: #fff;
                    padding: 1px 6px;
                    font-size: 10px;
                    font-weight: 600;
                    line-height: 1.4;
                    border-radius: 3px;
                    white-space: nowrap;
                    pointer-events: none;
                    z-index: 2;
                }
                .pt-vis-container .vis-label.vis-nested-group { background: transparent !important; }
                .pt-vis-container.pt-no-labels .vis-panel.vis-left,
                .pt-vis-container.pt-no-labels .vis-labelset {
                    width: 0 !important;
                    min-width: 0 !important;
                    overflow: hidden !important;
                    border-right: 0 !important;
                }
                .pt-vis-container.pt-no-labels .vis-labelset .vis-label {
                    display: none !important;
                }
            `}</style>
            <div ref={captureRef}>
                <div className="pt-vis-toolbar">
                    <div className="pt-toolbar-group">
                        <span className="pt-toolbar-label">
                            Depth
                            <span
                                className="pt-help"
                                title={DEPTH_OPTIONS.map(o => `${o.text}: ${o.hint}`).join('\n')}
                            >?</span>
                        </span>
                        <ButtonGroup
                            keyExpr="value"
                            items={DEPTH_OPTIONS}
                            selectedItemKeys={[p.depthMode]}
                            onItemClick={(e: any) => p.setDepthMode(e.itemData.value)}
                            stylingMode="outlined"
                            selectionMode="single"
                        />
                    </div>
                    <div className="pt-toolbar-group">
                        <div className="pt-filter">
                            <TagBox
                                dataSource={p.modules}
                                displayExpr="name"
                                valueExpr="key"
                                value={p.visibleModuleKeys}
                                onValueChanged={(e) => p.setVisibleModuleKeys(e.value ?? [])}
                                placeholder="Filter modules"
                                showSelectionControls={true}
                                applyValueMode="useButtons"
                            />
                        </div>
                    </div>
                    <div className="pt-toolbar-group" title="Zoom — or hold Ctrl and scroll on the timeline.">
                        <span className="pt-toolbar-label">Zoom</span>
                        <div style={{ width: 140 }}>
                            <Slider
                                min={0}
                                max={100}
                                step={1}
                                value={sliderValue}
                                onValueChanged={(e) => {
                                    // Track the drag locally for smooth thumb movement, then commit
                                    // on idle (~200ms after the last move = the user has released).
                                    draggedValueRef.current = e.value;
                                    setDraggedValue(e.value);
                                    if (idleTimerRef.current !== null) {
                                        window.clearTimeout(idleTimerRef.current);
                                    }
                                    idleTimerRef.current = window.setTimeout(() => {
                                        idleTimerRef.current = null;
                                        // Just commit. The effect on [p.zoomPercent] clears
                                        // draggedValue once the round-trip lands — avoids the
                                        // snap-back-then-forward bounce.
                                        if (draggedValueRef.current !== null) {
                                            setZoomPercentRef.current(draggedValueRef.current);
                                        }
                                    }, 200);
                                }}
                            />
                        </div>
                    </div>
                    <div className="pt-toolbar-actions">
                        <div className="pt-toggle-group">
                            <ReactButton
                                text="Labels"
                                type="normal"
                                stylingMode="outlined"
                                elementAttr={{ class: `pt-toggle ${p.showLabels ? 'pt-toggle-on' : 'pt-toggle-off'}` }}
                                onClick={() => p.setShowLabels(!p.showLabels)}
                                hint="Show/hide the row title column."
                            />
                            <ReactButton
                                text="Events"
                                type="normal"
                                stylingMode="outlined"
                                elementAttr={{ class: `pt-toggle ${p.showEvents ? 'pt-toggle-on' : 'pt-toggle-off'}` }}
                                onClick={() => p.setShowEvents(!p.showEvents)}
                                hint="Show/hide event ticks (files and comments)."
                            />
                            <ReactButton
                                text="Projected"
                                type="normal"
                                stylingMode="outlined"
                                elementAttr={{ class: `pt-toggle ${p.showProjected ? 'pt-toggle-on' : 'pt-toggle-off'}` }}
                                onClick={() => p.setShowProjected(!p.showProjected)}
                                hint="Show/hide projected date ranges."
                            />
                            <ReactButton
                                text="Undated"
                                type="normal"
                                stylingMode="outlined"
                                elementAttr={{ class: `pt-toggle ${p.showUnscheduled ? 'pt-toggle-on' : 'pt-toggle-off'}` }}
                                onClick={() => p.setShowUnscheduled(!p.showUnscheduled)}
                                hint="Show/hide rows for tasks/subtasks that have no start/end dates."
                            />
                        </div>
                        <ReactButton
                            text="Export PNG"
                            type="normal"
                            stylingMode="contained"
                            onClick={() => p.exportPng(captureRef.current)}
                        />
                    </div>
                </div>
                {p.data && (
                    <UnscheduledBanner
                        phases={p.undatedNotShown.phases}
                        steps={p.undatedNotShown.steps}
                        absorbed={p.absorbedNotShown}
                    />
                )}
                {!p.visibleModuleKeys.length && (
                    <div style={{ padding: '12px 4px', color: '#666', fontStyle: 'italic' }}>
                        All modules are filtered out. Select at least one to see the timeline.
                    </div>
                )}
                <div className={`pt-vis-container${p.showLabels ? '' : ' pt-no-labels'}`} ref={p.containerRef} style={{ width: '100%' }} />
                <div
                    className="pt-h-scroll"
                    ref={hScrollRef}
                    onScroll={onHScroll}
                    style={{ width: '100%', height: 14, overflowX: 'auto', overflowY: 'hidden' }}
                >
                    <div style={{ height: 1 }} />
                </div>
                <StatusLegend />
            </div>
            <EventTooltip
                hoverTip={p.hoverTip}
                onTipEnter={p.onTipEnter}
                onTipLeave={p.onTipLeave}
                downloadEventFile={p.downloadEventFile}
                openEventLocation={p.openEventLocation}
            />
        </div>
    );
};

export default VisTimelineView;
